/**
 * Users API Service
 * User management related API calls
 */

import { api } from '@/shared/api/apiClient';
import { API_ROUTES, buildUrl } from '@/config/apiRoutes';
import type { PaginatedResponse } from '@/shared/api/types';
import type { User, UserListParams } from '@/types/entities';

// Re-export types for convenience
export type { User, UserListParams };

// Local type alias for backward compatibility
export type UsersListParams = UserListParams;

// ===========================================
// Users Service
// ===========================================
export const usersApi = {
    /**
     * Get paginated registered users list
     */
    getRegisteredUsers: async (params: UsersListParams): Promise<PaginatedResponse<User>> => {
        const { page = 1, per_page = 10, search, status, email_verified } = params;
        const url = buildUrl(API_ROUTES.admin.users.base, {
            page,
            per_page,
            ...(search && { search }),
            ...(status && { status }),
            ...(email_verified !== undefined && { email_verified }),
        });
        return api.get<PaginatedResponse<User>>(url);
    },

    /**
     * Get single user by ID
     */
    getUserById: async (id: number | string): Promise<User> => {
        return api.get<User>(API_ROUTES.admin.users.getById(id));
    },

    /**
     * Update user status (PATCH - backend expects new_status field)
     */
    updateUserStatus: async (userId: number | string, status: string): Promise<User> => {
        return api.patch<User>(API_ROUTES.admin.users.updateStatus(userId), { new_status: status });
    },

    /**
     * Soft-delete a batch of users. The rows stay in the table and
     * show up in /manage/bin so the operator can restore mistakes.
     */
    bulkDeleteUsers: async (ids: number[]): Promise<{ deleted_count: number }> => {
        return api.post<{ deleted_count: number }>('/users/bulk-delete', { ids });
    },

    /** List soft-deleted users (Bin view). */
    listDeletedUsers: async (): Promise<{ items: Array<{
        id: number; name: string; email: string; phone: string | null;
        deleted_at: string | null;
    }>; total: number }> => {
        return api.get('/users/deleted');
    },

    /** Clear deleted_at so the user re-appears on Potential Leads. */
    restoreUser: async (userId: number): Promise<void> => {
        await api.post(`/users/${userId}/restore`);
    },

    /** Hard-delete a soft-deleted user (irreversible). */
    purgeUser: async (userId: number): Promise<void> => {
        await api.delete(`/users/${userId}/purge`);
    },
};

export default usersApi;
